import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { TONE_TEXT, type StatusTone } from '@/lib/widget-status';

/**
 * Byggesteiner for innholdet i DrillPanel (VIB-2990): likning av ekte tall
 * først, så seksjoner med rader, merknader og lenke til kildesiden.
 */

export type DrillTerm = string | { value: string; label?: string; tone?: StatusTone };

export function DrillEquation({ terms }: { terms: DrillTerm[] }) {
  return (
    <div className="flex flex-wrap items-end gap-x-3 gap-y-2 rounded-xl bg-muted px-4 py-3.5">
      {terms.map((t, i) =>
        typeof t === 'string' ? (
          <span key={i} className="pb-5 text-[18px] text-[#8A97A3]">{t}</span>
        ) : (
          <span key={i} className="flex flex-col">
            <span className={cn('font-display font-semibold text-[22px] leading-tight tabular-nums', t.tone && TONE_TEXT[t.tone])}>
              {t.value}
            </span>
            <span className="text-[12px] text-[#8A97A3] min-h-[18px]">{t.label}</span>
          </span>
        ),
      )}
    </div>
  );
}

export function DrillSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="mt-[22px]">
      <h3 className="pb-1.5 border-b border-foreground/[0.12] text-[11px] uppercase tracking-[0.06em] text-[#8A97A3] font-medium">
        {label}
      </h3>
      {children}
    </section>
  );
}

interface DrillRowProps {
  title: string;
  value: string;
  sub?: string;
  tone?: StatusTone;
  /** Åpner neste nivå i panelet. */
  onClick?: () => void;
  /** Lenke til kildesiden, for eksempel prosjektkortet. */
  to?: string;
}

export function DrillRow({ title, value, sub, tone, onClick, to }: DrillRowProps) {
  const body = (
    <>
      <span className="flex-1 min-w-0">
        <span className="block truncate font-medium">{title}</span>
        {sub && <span className="block truncate text-[12px] text-[#8A97A3]">{sub}</span>}
      </span>
      <span className={cn('shrink-0 font-semibold tabular-nums', tone && TONE_TEXT[tone])}>{value}</span>
      {(onClick || to) && <span aria-hidden className="text-[#8A97A3]">›</span>}
    </>
  );
  const cls = 'flex items-center gap-2.5 px-1.5 py-2.5 border-b border-foreground/[0.06] text-[14px]';
  const interactive = 'w-full text-left hover:bg-foreground/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';
  if (to) return <Link to={to} className={cn(cls, interactive)}>{body}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={cn(cls, interactive)}>{body}</button>;
  return <div className={cls}>{body}</div>;
}

export function DrillCallout({ children, tone = 'near' }: { children: ReactNode; tone?: 'near' | 'neutral' }) {
  return (
    <p
      className={cn(
        'mt-4 px-3.5 py-3 rounded-[10px] text-[13.5px] leading-normal',
        tone === 'near' ? 'bg-[rgba(224,146,47,0.1)] text-[#7A4A0E]' : 'bg-muted text-[#4A5A6A]',
      )}
    >
      {children}
    </p>
  );
}

export function DrillText({ children }: { children: ReactNode }) {
  return <p className="mt-3 text-[13.5px] text-[#4A5A6A] leading-normal">{children}</p>;
}

export function DrillSourceLink({ to, label }: { to: string; label: string }) {
  return (
    <div className="mt-5 pt-3.5 border-t border-foreground/[0.08] flex justify-end">
      <Link to={to} className="text-[14px] font-semibold text-primary hover:underline">
        {label} ›
      </Link>
    </div>
  );
}
